import { Marker } from 'react-map-gl';
import { Source, Layer } from 'react-map-gl';
import { useRef } from 'react';
import inside from 'point-in-polygon-hao';
import distance from '@turf/distance';
import OutletCluster from './OutletCluster';

const outletIsInsideConstituency = (outlet, activeConstituencyGeometry) => {
    if (activeConstituencyGeometry.type === "Polygon") {
        const insidePolygon = inside(outlet.LOCATION, activeConstituencyGeometry.coordinates);
        return insidePolygon;
    }
    else { //multi polygon
        let insidePolygon = false;
        activeConstituencyGeometry.coordinates.forEach(polygon => {
            if (inside(outlet.LOCATION, polygon))
                insidePolygon = true;
        })
        return insidePolygon;
    }
}

const outletOffscreen = (outlet, mapBounds) => {
    const mapPolygon = [
        [mapBounds._ne.lng, mapBounds._ne.lat],
        [mapBounds._ne.lng, mapBounds._sw.lat],
        [mapBounds._sw.lng, mapBounds._sw.lat],
        [mapBounds._sw.lng, mapBounds._ne.lat],
        [mapBounds._ne.lng, mapBounds._ne.lat],
    ]
    const onScreen = inside(outlet.LOCATION, [mapPolygon]);
    return !onScreen;
}

const findNearestPoint = (outlet, activeConstituencyGeometry) => {
    if (activeConstituencyGeometry.type === "Polygon") {
        let smallestDistance;
        let nearestPoint;

        activeConstituencyGeometry.coordinates[0].forEach(point => {
            const dist = distance(outlet.LOCATION, point, { units: "kilometers" })
            if (!nearestPoint || !smallestDistance || dist < smallestDistance) {
                smallestDistance = dist;
                nearestPoint = point;
            }
        })

        return nearestPoint;
    }
    else { //multi polygon
        let smallestDistance;
        let nearestPoint;
        activeConstituencyGeometry.coordinates.forEach(polygon => polygon[0].forEach(point => {
            const dist = distance(outlet.LOCATION, point, { units: "kilometers" })
            if (!nearestPoint || !smallestDistance || dist < smallestDistance) {
                smallestDistance = dist;
                nearestPoint = point;
            }
        }))

        return nearestPoint;
    }
}

const sortBySelectedAndHover = (outletPoints) => {
    // Sort the existing points so hovered/selected ones render last (on top)
    // without duplicating them in the React tree.
    return [...outletPoints].sort((a, b) => {
        const aActive = a.props.hover || a.props.selected ? 1 : 0;
        const bActive = b.props.hover || b.props.selected ? 1 : 0;
        return aActive - bActive;
    });
}

export const OutletMarker = ({ outlet, hoverOutlet, selectedOutlet, setHoverOutlet, setSelectedOutlet, markerClick, cluster }) => {
    const suppressHoverRef = useRef(false);
    const activeOutlet = (outlet === hoverOutlet || outlet === selectedOutlet);
    outlet.cluster = cluster;

    return <div
        className={`outlet-marker ${outlet.INDEPENDENCE ? "blue" : "orange"} ${activeOutlet && "active"}`}
        onMouseOver={() => {
            if (!suppressHoverRef.current) setHoverOutlet(outlet);
        }}
        onMouseLeave={() => {
            setHoverOutlet(null);
            suppressHoverRef.current = false;
        }}
        onClick={(event) => markerClick(outlet, event)}
    >
        <p className={`location-label outlet-tooltip ${activeOutlet && "active"}`}>{outlet.TITLE}{outlet === selectedOutlet && <span className='close-icon' onClick={(e) => { e.stopPropagation(); suppressHoverRef.current = true; setSelectedOutlet(null); setHoverOutlet(null); }}>x</span>}</p>
        {(outlet === selectedOutlet && !outlet.OWNER_LOCATION) &&
            <div className={`ownership-card ${cluster ? "cluster" : ""} ${selectedOutlet.INDEPENDENCE ? "blue" : "orange"}`}>
                <p className={`ownership-card-text ${selectedOutlet.INDEPENDENCE ? "blue" : "orange"}`}>Owned {selectedOutlet.INDEPENDENCE && "locally"} by <br /><b>{outlet.OWNER}</b></p>
            </div>}
    </div>
}

const OutletMarkers = ({ outlets, hoverOutlet, setHoverOutlet, activeConstituency, selectedOutlet, setSelectedOutlet, mapBounds, clusterActive, setClusterActive, boundaryType, boundaries }) => {
    const idKey = boundaryType === 'lad' ? 'LAD24CD' : 'PCON24CD';

    const activeConstituencyGeometry = activeConstituency && boundaries
        ? boundaries.features.find(constituency => constituency.properties[idKey] === activeConstituency[idKey])?.geometry
        : null;

    const outsideOutlets = [];

    const markerClick = (outlet, event) => {
        event.preventDefault();

        if (outlet !== selectedOutlet) {
            setSelectedOutlet(outlet);
        }
        else
            setSelectedOutlet(null)
    }

    const locationsWithOutlets = {};

    outlets.forEach(outlet => {
        if (locationsWithOutlets[outlet.LOCATION])
            locationsWithOutlets[outlet.LOCATION].push(outlet);
        else
            locationsWithOutlets[outlet.LOCATION] = [outlet];
    })

    const outletPoints = [];

    for (const location in locationsWithOutlets) {
        const outletsAtLocation = locationsWithOutlets[location];
        const isCluster = outletsAtLocation.length > 1;

        if (!isCluster) {
            const outlet = outletsAtLocation[0];

            if (outlet.LOCATION && !isNaN(outlet.LOCATION[0]) && !isNaN(outlet.LOCATION[1])) {
                if (activeConstituencyGeometry && !outletIsInsideConstituency(outlet, activeConstituencyGeometry) && mapBounds && outletOffscreen(outlet, mapBounds))
                    outsideOutlets.push(outlet);

                outletPoints.push(
                    <Marker
                        key={outlet.ID}
                        longitude={outlet.LOCATION[0]}
                        latitude={outlet.LOCATION[1]}
                        anchor={"center"}
                        selected={outlet === selectedOutlet}
                        hover={outlet === hoverOutlet}
                        title={outlet.TITLE}
                    >
                        <OutletMarker
                            outlet={outlet}
                            hoverOutlet={hoverOutlet}
                            selectedOutlet={selectedOutlet}
                            setHoverOutlet={setHoverOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                            markerClick={markerClick}
                            cluster={false}
                        />
                    </Marker >)
            }
        }
        else {
            const outlet = outletsAtLocation[0];
            const clusterHasSelected = outletsAtLocation.includes(selectedOutlet);
            const clusterHasHover = outletsAtLocation.includes(hoverOutlet);

            if (outlet.LOCATION && !isNaN(outlet.LOCATION[0]) && !isNaN(outlet.LOCATION[1])) {
                outletPoints.push(
                    <Marker
                        key={outlet.ID}
                        longitude={outlet.LOCATION[0]}
                        latitude={outlet.LOCATION[1]}
                        anchor={"center"}
                        selected={clusterHasSelected}
                        hover={clusterHasHover}
                    >
                        <OutletCluster
                            outlets={outletsAtLocation}
                            hoverOutlet={hoverOutlet}
                            selectedOutlet={selectedOutlet}
                            setHoverOutlet={setHoverOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                            markerClick={markerClick}
                            clusterActive={clusterActive}
                            setClusterActive={setClusterActive}
                        />
                    </Marker >)
            }
        }
    }

    const sortedOutletPoints = sortBySelectedAndHover(outletPoints);

    const outsideLinesIndy = {
        "type": "FeatureCollection",
        "features": []
    }

    const outsideLinesCorp = {
        "type": "FeatureCollection",
        "features": []
    }

    outsideOutlets.forEach(outlet => {
        const line = {
            "type": "Feature",
            "properties": {},
            "geometry": {
                "type": "LineString",
                "coordinates": [
                    findNearestPoint(outlet, activeConstituencyGeometry),
                    outlet.LOCATION
                ]
            }
        }
        if (outlet.INDEPENDENCE)
            outsideLinesIndy.features.push(line);
        else
            outsideLinesCorp.features.push(line);
    })

    const lineStyleIndy = {
        type: 'line',
        paint: {
            'line-color': '#265089',
            "line-width": 5,
            "line-dasharray": [2, 2]
        }
    }

    const lineStyleCorp = {
        type: 'line',
        paint: {
            'line-color': '#8e4a1c',
            "line-width": 5,
            "line-dasharray": [2, 2]
        }
    }

    return <>
        {sortedOutletPoints}
        <Source type="geojson" data={outsideLinesIndy}>
            <Layer {...lineStyleIndy} />
        </Source>
        <Source type="geojson" data={outsideLinesCorp}>
            <Layer {...lineStyleCorp} />
        </Source>
        {selectedOutlet && selectedOutlet.OWNER_LOCATION && !isNaN(selectedOutlet.OWNER_LOCATION[0]) && <><Marker
            longitude={selectedOutlet.OWNER_LOCATION[0]}
            latitude={selectedOutlet.OWNER_LOCATION[1]}
            anchor={"center"}
        >
            <span className={`outlet-owner-marker ${selectedOutlet.INDEPENDENCE ? "blue" : "orange"} ${selectedOutlet.cluster ? "cluster" : ""}`} />
            <div
                className={`outlet-owner-marker ${selectedOutlet.INDEPENDENCE ? "blue" : "orange"} ${selectedOutlet.cluster ? "cluster" : ""}`}
            >
                <div className={`ownership-card ${selectedOutlet.cluster ? "cluster" : ""} ${selectedOutlet.INDEPENDENCE ? "blue" : "orange"}`}>
                    <p className={`ownership-card-text ${selectedOutlet.INDEPENDENCE ? "blue" : "orange"}`}>Owned by <br /><b>{selectedOutlet.OWNER}</b></p>
                </div>
            </div>
        </Marker >
            <Source type="geojson" data={{
                "type": "FeatureCollection",
                "features": [{
                    "type": "Feature",
                    "properties": {},
                    "geometry": {
                        "type": "LineString",
                        "coordinates": [
                            selectedOutlet.LOCATION,
                            selectedOutlet.OWNER_LOCATION
                        ]
                    }
                }]
            }}>
                <Layer type={'line'}
                    paint={
                        {
                            'line-color': selectedOutlet.INDEPENDENT ? '#265089' : '#8e4a1c',
                            "line-width": 5,
                            "line-dasharray": [2, 2]
                        }
                    }
                />
            </Source>
        </>}
    </>
}

export default OutletMarkers;